import { Students } from '@components/Modal/GroupProfileModal/style'
import { GET_DATA } from '@components/Modal/SingleProfileModal/QuerySingleProfile'
import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { Material } from '@interfaces/Material'
import { GroupClassList } from '@interfaces/MyLesson'
import { GroupClassListItem, InstructorGroupInfo } from '@interfaces/MySchedule'
import { StudentProfileInfo } from '@interfaces/Student'
import { GET_LEARNING_PATH, GET_LESSON_GROUPS, GET_MATERIAL } from '@queries/index'
import { MY_BERLITZ_ENV } from '@utils/constants'
import { get } from 'lodash'
import find from 'lodash/find'
import groupBy from 'lodash/groupBy'
import head from 'lodash/head'
import isEqual from 'lodash/isEqual'
import map from 'lodash/map'
import mapValues from 'lodash/mapValues'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import React from 'react'
import { withApollo, WithApolloClient } from 'react-apollo'
import { compose } from 'redux'
import { LessonDate } from './Layout/interface'

interface QueryPageResults {
  loading: boolean
  refetch: Function
  data: InstructorGroupInfo[]
  loadingLessonDates: boolean
  lessonDates: LessonDate[]
}

interface QueryPageDataProp {
  InstructorId: string
  StartDateTime: string
  EndDateTime: string
  withLessonDates?: boolean
  children: (results: QueryPageResults) => {}
}

export const GET_PAGE_DATA = GET_LESSON_GROUPS

const getDateLessonCount = (
  GroupClassList: GroupClassListItem[],
  toUserDate: (date: string, format?: string, displayFormat?: string) => string
) => {
  let lessonDates: LessonDate[] = []

  mapValues(
    groupBy(GroupClassList.map(({ StartTime }) => toUserDate(StartTime, undefined, 'YYYY-MM-DD'))),
    (dates = [], key) => {
      lessonDates = [
        ...lessonDates,
        {
          date: key,
          numberOfLessons: dates.length,
        },
      ]
    }
  )

  return lessonDates
}

export type Props = WithApolloClient<QueryPageDataProp & WithUserTimeProps & WithLessonUtilsProp>

export class QueryPageData extends React.Component<Props> {
  _isMounted = false
  lessonAbortCtrl = new AbortController()
  materialAbortCtrl = new AbortController()
  prevMaterialAbortCtrl = new AbortController()
  datesAbortCtrl = new AbortController()
  learningPathAbortCtrl = new AbortController()

  state = {
    loading: false,
    data: [] as InstructorGroupInfo[],
    loadingLessonDates: false,
    lessonDates: [] as LessonDate[],
    studentLoading: false,
    students: [] as StudentProfileInfo[],
  }

  async componentDidMount() {
    this._isMounted = true
    this.fetchAllData()
  }

  async componentDidUpdate(prevProps) {
    const { StartDateTime, EndDateTime } = this.props
    if (
      (this._isMounted && !isEqual(prevProps.StartDateTime, StartDateTime)) ||
      !isEqual(prevProps.EndDateTime, EndDateTime)
    ) {
      this.fetchAllData()
    }
  }

  fetchAllData = () => {
    this.fetchData()

    if (this.props.withLessonDates) {
      this.fetchLessonDates()
    }
  }

  fetchStudents = async (groups: GroupClassListItem[]) => {
    const { client } = this.props
    this.setState({ studentLoading: true })
    const promisedStudents = groups
      .filter(group => group?.SessionInfoList?.[0]?.CustomerId || group?.StudentGroupId)
      .map(async group => {
        const StudentId = group?.SessionInfoList?.[0]?.CustomerId || group?.StudentGroupId
        const params = {
          StudentId,
          Language: group.Language,
        }

        const studentRes = await client.query<any>({
          query: GET_DATA,
          variables: params,
        })

        const student = (studentRes.data?.getStudents?.[0]?.StudentProfileInfo ?? {}) as StudentProfileInfo
        return {
          ...student,
          StudentId,
        }
      })
    const students = await Promise.all(promisedStudents)
    this.setState({ studentLoading: true, students })
    return students
  }

  componentWillUnmount() {
    this._isMounted = false
    this.lessonAbortCtrl.abort()
    this.materialAbortCtrl.abort()
    this.datesAbortCtrl.abort()
    this.learningPathAbortCtrl.abort()
  }

  fetchLessonDates = async () => {
    const { client, InstructorId, StartDateTime, EndDateTime, toUserDate } = this.props

    this.setState({ loadingLessonDates: true })

    try {
      const params = {
        InstructorId,
        StartDateTime: moment
          .utc(
            moment(StartDateTime)
              .startOf('month')
              .startOf('week')
          )
          .format(),
        EndDateTime: moment
          .utc(
            moment(EndDateTime)
              .endOf('month')
              .endOf('week')
          )
          .format(),
        FILTER_TYPE: 'Groups',
      }

      const res = await client.query({
        query: GET_PAGE_DATA,
        variables: { params },
        fetchPolicy: 'network-only',
        context: {
          fetchOptions: {
            signal: this.datesAbortCtrl.signal,
          },
        },
      })

      let lessons = res?.data?.getLessonGroups?.[0]?.InstructorGroupInfo?.GroupClassList ?? []
      lessons = this.props.onlyValidLessons(lessons)

      if (this._isMounted) {
        this.setState({
          lessonDates: getDateLessonCount(lessons, toUserDate),
          loadingLessonDates: false,
        })
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ loadingLessonDates: false })
      }
    }
  }

  fetchData = async () => {
    this.setState({ loading: true })
    const attachNoOfLessons = (studs: StudentProfileInfo[]) => (group: any) => {
      const StudentId = group?.SessionInfoList?.[0]?.CustomerId
      const program = studs
        .find(stud => stud?.StudentId === StudentId)
        ?.ProgramStudentWrappers.find(prog => prog.ProgramStudentInfo.ProgramId === group.ProgramId)
      return {
        ...group,
        TotalLessons: program?.ProgramStudentInfo?.TotalLessons ?? 0,
        LessonsTaken: program?.ProgramStudentInfo?.LessonsTaken ?? 0,
        LessonsRemaining: program?.ProgramStudentInfo?.LessonsRemaining ?? 0,
      }
    }
    try {
      const response = await this.getGroupDetail()

      let data: InstructorGroupInfo[] = []

      if (response.length > 0) {
        const lessonGroup: any = head(response)
        const classList = get(lessonGroup, 'InstructorGroupInfo.GroupClassList', []) as GroupClassListItem[]
        const students = await this.fetchStudents(classList)

        data = [
          {
            ...lessonGroup,
            InstructorGroupInfo: {
              ...(lessonGroup?.InstructorGroupInfo ?? {}),
              GroupClassList: lessonGroup?.InstructorGroupInfo.GroupClassList.map(attachNoOfLessons(students)),
            },
          },
        ]
      }
      if (this._isMounted) {
        this.setState({ data, loading: false })
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ loading: false })
      }
    }
  }

  getGroupDetail = async (): Promise<InstructorGroupInfo[]> => {
    const { client, InstructorId, StartDateTime, EndDateTime } = this.props

    const params = {
      InstructorId,
      StartDateTime,
      EndDateTime,
      FILTER_TYPE: 'Groups',
    }

    const res = await client.query({
      query: GET_PAGE_DATA,
      variables: { params },
      fetchPolicy: 'network-only',
      context: {
        fetchOptions: {
          signal: this.materialAbortCtrl.signal,
        },
      },
    })

    const groups: InstructorGroupInfo[] = res?.data?.getLessonGroups ?? []
    return groups.map(group => {
      const lessons: GroupClassListItem[] = group?.InstructorGroupInfo?.GroupClassList ?? []
      const filteredLesson = this.props.onlyValidLessons(lessons)
      const addSessionMetaData = (mlesson: GroupClassListItem) => {
        const { StartTime, EndTime, Index } = this.props.getSessionMetadata(lessons, mlesson)

        const SessionMetaData = {
          StartTime,
          EndTime,
          Index,
        }

        const Type = mlesson.Type === 'Gap Filler' ? mlesson.GapFiller : mlesson.Type

        return {
          ...mlesson,
          Type,
          SessionMetaData,
        }
      }

      const addCounsellingIndicator = (accumulator: GroupClassListItem[], lesson: GroupClassListItem) => {
        const accLesson = accumulator.find(l => l.SessionId === lesson.SessionId)
        if (accLesson) {
          accumulator = accumulator.map(l => {
            if (l.SessionId === accLesson.SessionId) {
              return {
                ...l,
                ForCounselling: false,
              }
            }

            return l
          })
        }

        accumulator = accumulator.concat({
          ...lesson,
          ForCounselling: true,
        })

        return accumulator
      }

      group.InstructorGroupInfo.GroupClassList = orderBy(filteredLesson, ['StartTime'])
        .map(addSessionMetaData)
        .reduce(addCounsellingIndicator, [] as GroupClassListItem[])
      return group
    })
  }

  getMaterial = async (Materials: Material[]): Promise<Material[]> => {
    const { client } = this.props
    const promisedMaterials = map(Materials, async material => {
      const materialInfo = await client.query({
        query: GET_MATERIAL,
        variables: { Id: material.Material },
        fetchPolicy: 'network-only',
        context: {
          fetchOptions: {
            signal: this.materialAbortCtrl.signal,
          },
        },
      })

      const units = materialInfo?.data?.getLearningPathById?.units
      const unit = find(units, { unitid: parseInt(material.Chapter, 10) })
      return {
        ...material,
        Name: materialInfo?.data?.getLearningPathById?.fullname?.[0]?.fullname ?? material?.Material,
        ChapterName: unit?.fullname,
      }
    })

    return Promise.all(promisedMaterials)
  }

  getMaterialName = async (gcList: GroupClassListItem[], key: string): Promise<GroupClassListItem[]> => {
    const promisedGroups = map(gcList, async group => {
      const list = await this.getMaterial(group[key])
      return {
        ...group,
        [key]: list,
      }
    })

    return Promise.all(promisedGroups)
  }

  getLearningPath = async (GroupClassList: GroupClassListItem[]): Promise<GroupClassListItem[]> => {
    const { client } = this.props

    const promisedGroups = map(GroupClassList, async group => {
      const promisedMaterialOptions = map(group.SessionInfoList, async session => {
        let options = []
        if (!session.CustomerId) {
          return options
        }

        const res = await client.query({
          query: GET_LEARNING_PATH,
          variables: {
            StudentId: session.CustomerId,
            IdType: 'Instructor',
            isFlex: true,
            berlitzEnv: session?.MyBerlitzEnvironment ?? MY_BERLITZ_ENV.DEFAULT,
          },
          fetchPolicy: 'network-only',
          context: {
            fetchOptions: {
              signal: this.learningPathAbortCtrl.signal,
            },
          },
        })

        const data: any = res?.data

        if (data?.getLearningPath) {
          const lp = data.getLearningPath.map(learning => ({
            Chapter: '',
            Id: `${learning.lpid}`,
            Name: learning?.details?.fullname?.[0]?.fullname,
            Page: '',
            Point: '',
            Type: 'LearningPath',
            Units: map(learning?.details?.units ?? [], unit => ({
              Id: unit.unitid,
              Name: unit.fullname,
              URL: find(unit.activities, { type: 'ig' })?.url,
            })),
          }))
          options = options.concat(lp)
        }

        return options
      })

      const promisedOptions = await Promise.all(promisedMaterialOptions)

      return {
        ...group,
        MaterialOptions: promisedOptions[0],
      }
    })

    return Promise.all(promisedGroups)
  }

  render() {
    const { children } = this.props
    const { loading, data, loadingLessonDates, lessonDates } = this.state

    return children({
      loading,
      data,
      refetch: this.fetchAllData,
      loadingLessonDates,
      lessonDates,
    })
  }
}

export { getDateLessonCount }

export default compose(WithUserTimeHOC, WithLessonUtilsHOC, withApollo)(QueryPageData)
